import { useState } from "react";
import { C, PageHeader, GlassCard, Badge, Skeleton, StatusDot } from "@coremonit/design-system";
import { GitFork, ArrowUpRight, ArrowDownRight, AlertTriangle, TrendingUp } from "lucide-react";
import { useServiceDependencies, useServices } from "../api/hooks";

export function DependenciesPage() {
  const services = useServices();
  const [serviceId, setServiceId] = useState<string | undefined>();
  const deps = useServiceDependencies(serviceId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader icon={<GitFork size={16} />} title="Dependency Analysis" subtitle="Upstream, downstream, critical dependencies, blast radius." />
      <GlassCard style={{ padding: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ color: C.textMuted, fontSize: 11 }}>Service</span>
          <select value={serviceId ?? ""} onChange={(e) => setServiceId(e.target.value || undefined)}
            style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "6px 10px", fontSize: 12, minWidth: 260 }}>
            <option value="">Select a service…</option>
            {services.data?.items.map((s) => <option key={s.id} value={s.id} style={{ background: C.surface }}>{s.name}</option>)}
          </select>
        </div>
      </GlassCard>

      {!serviceId ? (
        <GlassCard style={{ padding: 40, textAlign: "center", color: C.textMuted, fontSize: 12 }}>Pick a service to inspect its blast radius.</GlassCard>
      ) : deps.isLoading ? <Skeleton height={280} /> : deps.data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
            <StatCard label="Upstream" value={deps.data.upstream.length} color={C.blue} icon={<ArrowUpRight size={12} />} />
            <StatCard label="Downstream" value={deps.data.downstream.length} color={C.purple} icon={<ArrowDownRight size={12} />} />
            <StatCard label="Critical edges" value={deps.data.critical.length} color={C.red} icon={<AlertTriangle size={12} />} />
            <StatCard label="Blast radius" value={deps.data.blastRadius} color={C.amber} icon={<TrendingUp size={12} />} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <SideCard title="Upstream (who calls me)" items={deps.data.upstream} />
            <SideCard title="Downstream (who I call)" items={deps.data.downstream} />
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color, icon }: { label: string; value: number; color: string; icon: React.ReactNode }) {
  return (
    <GlassCard style={{ padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, color: C.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>{icon}{label}</div>
      <div style={{ color, fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </GlassCard>
  );
}

function SideCard({ title, items }: { title: string; items: Array<{ id: string; name: string; status: string; kind: string }> }) {
  return (
    <GlassCard style={{ padding: 14 }}>
      <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>{title}</div>
      {items.length === 0 ? <div style={{ color: C.textMuted, fontSize: 11, padding: 12 }}>No matches.</div> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((s) => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <StatusDot status={s.status} />
                <div>
                  <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{s.name}</div>
                  <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>{s.kind}</div>
                </div>
              </div>
              <Badge color={s.status === "critical" ? "red" : s.status === "warning" ? "amber" : "green"}>{s.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}
